import { Stack } from '@src/common/components/Stack';
import { TextInput } from '@src/common/components/TextInput';
import { Typography } from '@src/common/components/Typography';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import clsx from 'clsx';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface Props<T extends FieldValues> {
  control: Control<T>;
  fieldName: Path<T>;
  disabled?: boolean;
  label?: Path<T> | string;
  ariaLabel?: string;
  placeholder?: string;
  helperText?: string;
  withoutArrows?: boolean;
  hiddenLabel?: boolean;
  tabIndex?: number;
  step?: number;
}

export const ControllerFormNumberInput = <T extends FieldValues>({
  control,
  fieldName,
  label,
  disabled,
  ariaLabel,
  placeholder,
  helperText,
  withoutArrows,
  hiddenLabel,
  tabIndex,
  step,
}: Props<T>) => {
  const { t } = useTranslation();

  return (
    <Controller
      name={fieldName}
      control={control}
      render={({
        field,
        fieldState: { error },
        formState: { disabled: formDisabled },
      }) => (
        <Stack>
          <TextInput
            {...field}
            value={field.value || ''}
            readOnly={disabled || formDisabled}
            className={clsx(fullWidth, withoutArrows && withoutArrowsStyle)}
            type="number"
            label={hiddenLabel ? '' : (label ?? t(`fields.${field.name}`))}
            placeholder={placeholder}
            error={error && t(`formErrors.${error!.message}`)}
            slotProps={{
              htmlInput: {
                'aria-label': ariaLabel,
                tabIndex,
                step,
                readOnly: disabled || formDisabled,
              },
            }}
            hiddenLabel={hiddenLabel}
          />
          {helperText && (
            <Stack paddingX={theme.spacing['4']} paddingY={theme.spacing['1']}>
              <Typography style={{ ...theme.typography.body.small }}>
                {helperText}
              </Typography>
            </Stack>
          )}
        </Stack>
      )}
    />
  );
};

const fullWidth = css({
  flex: 1,
});

const withoutArrowsStyle = css`
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;
